import Component from '../abstracts/Component';

export default class DropdownSizeSelectorComponent extends Component {

  get COMPONENTNAME() {
    return 'DropdownSizeSelectorComponent';
  }

  get SELECTORS() {
    return {
      sizeError: '[data-sizes-error]',
      main: 'select[name="size"]'
    };
  }

  get CLASSES() {
    return {
      hidden: '--hidden',
      noFixedHeight: '--no-fixed-height',
      pdpSizes: 'c-pdp-sizes',
      show: 'show',
      disabled: '--disabled'
    };
  }

  constructor(compEl) {
    super(compEl);
    this.sizeError = this.$component.parentElement.querySelector(this.SELECTORS.sizeError);
    this.main = this.$component.querySelector(this.SELECTORS.main);
    this.productCode = this.main.dataset.productCode;
  }

  /**
   * Clears the selected size value from the dropdown selector.
   * Resets the Select2 element to null and triggers a change event to update the UI.
   *
   * @returns {void}
   */
  cleanSelectSize() {
    this.select2elem.val(null).trigger('change');
  }

  /**
   * Initializes a Select2 dropdown component for size selection with custom templates and event handlers.
   *
   * @param {HTMLElement} elem - The select element to be converted into a Select2 dropdown
   * @description
   * - Configures Select2 with custom styling and templates for three states:
   *   - Last available item (displays last available message)
   *   - Unavailable item (displays email icon)
   *   - Regular available item
   * - Handles size selection events:
   *   - Emits 'notifyme' event for unavailable products
   *   - Emits 'notifymeclose' event for available products
   *   - Emits 'sizechanged' event with product details
   * - Resets any existing errors after selection
   * @fires PRODUCT_EVENTS.notifyme - When an unavailable size is selected
   * @fires PRODUCT_EVENTS.notifymeclose - When an available size is selected
   * @fires PRODUCT_EVENTS.sizechanged - When any size is selected, containing productCode, baseProductCode, sizeSelected, and size element
   */
  initSelect2(elem) {
    let ctx = this;
    const placeholder = elem.dataset.defaultLabel;
    this.select2elem = $(elem);
    // init select 2 with options
    this.select2elem.select2({
      width: '100%',
      minimumResultsForSearch: -1,
      dropdownCssClass: 'c-pdp-sizes__select__dropdown',
      placeholder: placeholder,
      dropdownParent: this.$component,
      escapeMarkup: (markup) => {
        return markup;
      },
      templateSelection: (option) => {
        if (option.element) {
          if (option.element.dataset.lastAvailable == 'true') {
            return `<div class="c-pdp-sizes__select__item --last-available">
                      <span class="c-pdp-sizes__select__item--value">${option.text}</span>
                      <span class="c-pdp-sizes__select__item--options">${option.element.dataset.lastAvailableMsg}</span>
                    </div>`;
          } else if (option.element.dataset.unavailable == 'true') {
            return `<div class="c-pdp-sizes__select__item --unavailable">
                      <span class="c-pdp-sizes__select__item--value">${option.text}</span>
                      <span class="c-pdp-sizes__select__item--options">
                        <i class="icon-mail"></i>
                      </span>
                    </div>`;
          } else {
            return `<div class="c-pdp-sizes__select__item">
                      <span class="c-pdp-sizes__select__item--value">${option.text}</span>
                      <span class="c-pdp-sizes__select__item--options"></span>
                    </div>`
          }
        } else {
          return option.text;
        }
      },
      templateResult: (option) => {
        if (option.element) {
          if (option.element.dataset.lastAvailable == 'true') {
            return `<div class="c-pdp-sizes__select__item --last-available">
                      <span class="c-pdp-sizes__select__item--value">${option.text}</span>
                      <span class="c-pdp-sizes__select__item--options">${option.element.dataset.lastAvailableMsg}</span>
                    </div>`;
          } else if (option.element.dataset.unavailable == 'true') {
            return `<div class="c-pdp-sizes__select__item --unavailable">
                      <span class="c-pdp-sizes__select__item--value">${option.text}</span>
                      <span class="c-pdp-sizes__select__item--options">
                        <i class="icon-mail"></i>
                      </span>
                    </div>`;
          } else {
            return `<div class="c-pdp-sizes__select__item">
                      <span class="c-pdp-sizes__select__item--value">${option.text}</span>
                      <span class="c-pdp-sizes__select__item--options"></span>
                    </div>`;
          }
        }
      }
    });

    this.select2elem.on('select2:select', (e) => {
      const data = e.params.data;
      if (data.element.dataset.unavailable == 'true') {
        ctx.$emit(ctx.$customEvents.PRODUCT_EVENTS.notifyme, {
          productCode: ctx.productCode
        })
      } else {
        ctx.$emit(ctx.$customEvents.PRODUCT_EVENTS.notifymeclose, {
          productCode: ctx.productCode
        })
      }
      ctx.$emit(ctx.$customEvents.PRODUCT_EVENTS.sizechanged, {
        productCode: data.element.dataset.productCode,
        baseProductCode: ctx.productCode,
        sizeSelected: data.element.value,
        size: data.element
      });
      // reset error
      ctx.resetError();
    });
  }

  /**
   * Resets the size error display by clearing the error message content,
   * removing the 'show' class, and removing the 'noFixedHeight' class from
   * the parent element if it exists.
   *
   * @returns {void}
   */
  resetError() {
    if (this.sizeError) {
      this.sizeError.innerHTML = '';
      this.sizeError.classList.remove(this.CLASSES.show);
      const parentElement = this.sizeError.parentElement.parentElement;
      if (parentElement && parentElement.classList.contains(this.CLASSES.noFixedHeight)) {
        parentElement.classList.remove(this.CLASSES.noFixedHeight);
      }
    }
  }

  bindEvents() {
    // on product added to cart reset select
    this.$on(this.$customEvents.PRODUCT_EVENTS.addedtocart, ({payload}) => {
      if (payload.baseProductCode === this.main.dataset.productCode) {
        // reset value
        this.cleanSelectSize();
      }
    });

    // on notify me success reset select
    this.$on(this.$customEvents.PRODUCT_EVENTS.notifymesuccess, () => {
      // reset value
      this.cleanSelectSize();
    });

    // size selected event
    this.$on(this.$customEvents.PRODUCT_EVENTS.sizerequired, (e) => {
      let sizeErrorBoxes = document.querySelectorAll(`[data-sizes-error=prod-${e.payload.dataset.code}]`);
      // put error
      sizeErrorBoxes.forEach(sizeErrorBox => {
        if (sizeErrorBox && !sizeErrorBox.classList.contains(this.CLASSES.show)) {
          // if we are in complete the look, we also need to change parent height in order to see error message
          const parentElem = sizeErrorBox.parentElement.parentElement;
          if (parentElem && parentElem.classList.contains(this.CLASSES.pdpSizes)) {
            parentElem.classList.add(this.CLASSES.noFixedHeight);
          }
          sizeErrorBox.classList.add(this.CLASSES.show);
          sizeErrorBox.innerHTML = e.payload.attributes['data-notification'].nodeValue;
        }
      });
    });

    this.$on(this.$customEvents.SIZES_EVENTS.disableAll, () => {
      this.$component.classList.add(this.CLASSES.disabled);
    });

    this.$on(this.$customEvents.SIZES_EVENTS.enableAll, () => {
      this.$component.classList.remove(this.CLASSES.disabled);
    });
  }

  render() {
    let ctx = this;
    this.log('Rendering...');
    this.bindEvents();
    this.moduleLoader.loadSelect2(
      (module) => {
        ctx.initSelect2(this.main);
      },
      (error) => {
        console.log("failed to load select2 ", error.stack);
      }
    );
  }
}
